import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { authAPI } from '@/lib/auth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { AlertCircle, Loader2, CheckCircle2, ArrowLeft } from 'lucide-react';

export default function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Normalize email to lowercase
      const normalizedEmail = email.toLowerCase().trim();
      const result = await authAPI.forgotPassword(normalizedEmail);
      
      // Log debug info if present (for troubleshooting email issues)
      if (result._debug) {
        console.warn('[ForgotPassword] Supabase returned an error:', result._debug);
      }
      
      setSuccess(true);
    } catch (err) {
      console.error('[ForgotPassword] Unexpected error:', err);
      // Still show success to prevent email enumeration
      setSuccess(true);
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-[#050816] flex items-center justify-center px-4 py-12">
        <div className="absolute inset-0 bg-gradient-to-b from-[#0f1724] to-[#050816]" />
        
        <Card className="w-full max-w-md relative z-10 bg-[#0f1724] border-white/10">
          <CardHeader className="text-center">
            <div className="mx-auto w-16 h-16 bg-[#C6A14A]/20 rounded-full flex items-center justify-center mb-4">
              <CheckCircle2 className="h-8 w-8 text-[#C6A14A]" />
            </div>
            <CardTitle className="text-2xl font-light text-white">
              Check Your Email
            </CardTitle>
            <CardDescription className="text-[#E8E4DE]/60">
              If an account exists for <span className="text-white">{email}</span>, we've sent password reset instructions.
            </CardDescription>
          </CardHeader>
          <CardFooter className="flex flex-col gap-4">
            <Button
              asChild
              variant="outline"
              className="w-full border-white/10 text-[#E8E4DE] hover:bg-white/5 h-11"
            >
              <Link to="/login">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Sign In
              </Link>
            </Button>
          </CardFooter>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050816] flex items-center justify-center px-4 py-12">
      <div className="absolute inset-0 bg-gradient-to-b from-[#0f1724] to-[#050816]" />
      
      <Card className="w-full max-w-md relative z-10 bg-[#0f1724] border-white/10">
        <CardHeader className="space-y-1 text-center">
          <Link to="/" className="inline-block mb-4">
            <img 
              src="https://customer-assets.emergentagent.com/job_page-builder-81/artifacts/a7jf2g4n_RCSTUDIOS-logo-h240.png"
              alt="RC Studios" 
              className="h-8 mx-auto"
              style={{ filter: 'brightness(0) invert(1)' }}
            />
          </Link>
          <CardTitle className="text-2xl font-light text-white tracking-wide">
            Forgot Password?
          </CardTitle>
          <CardDescription className="text-[#E8E4DE]/60">
            Enter your email and we'll send you a reset link
          </CardDescription>
        </CardHeader>
        
        <form onSubmit={handleSubmit}>
          <CardContent className="space-y-4">
            {error && (
              <div className="flex items-center gap-2 p-3 text-sm text-red-400 bg-red-400/10 border border-red-400/20 rounded-lg">
                <AlertCircle className="h-4 w-4 shrink-0" />
                <span>{error}</span>
              </div>
            )}
            
            <div className="space-y-2">
              <Label htmlFor="email" className="text-[#E8E4DE]/80 text-sm">
                Email Address
              </Label>
              <Input
                id="email"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={loading}
                className="bg-[#050816]/50 border-white/10 text-white placeholder:text-white/30 focus:border-[#C6A14A] focus:ring-[#C6A14A]/20"
                data-testid="forgot-password-email-input"
              />
            </div>
          </CardContent>
          
          <CardFooter className="flex flex-col gap-4">
            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-[#C6A14A] text-[#050816] hover:bg-[#D4AF5A] font-medium h-11"
              data-testid="forgot-password-submit-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Sending...
                </>
              ) : (
                'Send Reset Link'
              )}
            </Button>
            
            <Button
              asChild
              variant="ghost"
              className="w-full text-[#E8E4DE]/60 hover:text-white hover:bg-white/5"
            >
              <Link to="/login">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Sign In
              </Link>
            </Button>
          </CardFooter>
        </form>
      </Card>
    </div>
  );
}
